import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import eq from 'webapp/helpers/eq';
import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type SourceType from 'webapp/models-custom/source-type';

import type { onConnectSourceResult } from 'webapp/services/connect';
import type { TrackingOriginNewConnectionInitiated } from 'webapp/services/model-manager/source-types';

export interface Args {
	model: SourceType;
	isOverlayHidden?: boolean;
	hideConnectedFlag?: boolean;
	trackingOrigin?: TrackingOriginNewConnectionInitiated;
	showConnectLoader?: boolean;
	activeFilterWords?: string[];
	onClick?: (model: SourceType) => void;
}

export default class DataManagerGridItem<T extends Args> extends Component<T> {
	@service('connect') declare connectService: Service.Connect;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	_tooltipWithIcon: { icon: string; text?: string; maxWidth?: number } | null = null;

	@tracked submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.showConnectLoader) {
			this.eventManagerService.on(AppEvent.CONNECT_INITIALIZATION_FINISHED, this, () => {
				this.submitInProgress = false;
			});
		}
	}

	get classNames(): string {
		let classNames = ['card', 'card--source-type', 'flex-item', 'filter-sort-item'];

		if (this.isAlreadyConnected && !this.args.hideConnectedFlag) {
			classNames.push('card--connected');
		}

		if (this.args.model.animationClass) {
			classNames.push(this.args.model.animationClass);
		}

		if (!this.isOverlayHidden) {
			classNames.push('card--has-overlay');
		}

		if (this.extraClassNames.length) {
			classNames = [...classNames, ...this.extraClassNames];
		}

		return classNames.join(' ');
	}

	get extraClassNames(): string[] {
		return [];
	}

	get isOverlayHidden(): boolean {
		return this.args.isOverlayHidden || this.submitInProgress;
	}

	get isAlreadyConnected(): boolean {
		return this.args.model.isAlreadyConnected && !this.args.model.isWithCustomToken;
	}

	get isChecked(): boolean {
		return this.isAlreadyConnected;
	}

	get activeFilterWords() {
		return this.args.activeFilterWords ?? [];
	}

	get tooltipWithIcon() {
		let tooltipWithIcon = null;
		const _tooltipWithIcon = this._tooltipWithIcon;

		if (_tooltipWithIcon) {
			tooltipWithIcon = _tooltipWithIcon;
		} else if (this.isChecked) {
			tooltipWithIcon = {
				icon: 'connected',
			};
		}

		return tooltipWithIcon;
	}

	@action
	triggerClick(): void {
		if (this.args.showConnectLoader) {
			this.submitInProgress = true;
		}

		const trackingOrigin = decodeURIComponent(window.location.hash).includes('dataset')
			? 'datasets'
			: undefined;

		const model = this.args.model;

		if (this.args.onClick) {
			this.args.onClick(model);
		} else {
			const skipSourceTypeInfoModal = this.args.model.isCustomRESTApi;

			if (skipSourceTypeInfoModal) {
				if (this.args.trackingOrigin) {
					this.mmSourceTypesService.triggerNewConnectionInitiated(
						model,
						this.args.trackingOrigin,
					);
				}

				const onConnectSuccess = this.connectService.getAfterConnectAction();
				void model
					.connectNew({ origin: 'datamanagerAvailable' })
					.then((result: onConnectSourceResult) => {
						if (result.isSuccess) {
							onConnectSuccess(model, +result.source.id, result.metadata);
						}
					});
			} else {
				this.mmSourceTypesService.goToSourceTypeInfo(this.args.model, trackingOrigin);
			}
		}
	}

	override willDestroy(): void {
		super.willDestroy();
		if (this.args.showConnectLoader) {
			this.eventManagerService.off(AppEvent.CONNECT_INITIALIZATION_FINISHED, this);
		}
	}
	<template>
		<li
			data-key={{@model.key}}
			role="button"
			class={{this.classNames}}
			{{on "click" this.triggerClick}}
		>

			<div class="info">
				<img src={{@model.avatar}} alt={{@model.name}} />

				<div class="info__title">
					{{highlightResult text=@model.name match=this.activeFilterWords}}
				</div>
				{{#if this.submitInProgress}}
					<BarLoaderComponent />
				{{else}}
					{{#if @model.shownBadges}}
						{{#each @model.shownBadges as |badge|}}
							<div class="info__tag info__tag--{{badge}}">
								{{#if (eq badge "datasets")}}
									<CommonUiIconComponent @name="Table" @size="sm" />
								{{else}}
									{{badge}}
								{{/if}}
							</div>
						{{/each}}
					{{/if}}

					{{#if this.tooltipWithIcon}}
						<span class={{joinClasses (array "icon" this.tooltipWithIcon.icon)}}>
							{{#if (eq this.tooltipWithIcon.icon "connected")}}
								<CommonUiIconComponent @name="Check" @size="xs" />
							{{/if}}
							{{#if this.tooltipWithIcon.text}}
								<ToolTipComponent
									@text={{this.tooltipWithIcon.text}}
									@theme="light"
									@maxWidth={{this.tooltipWithIcon.maxWidth}}
									@interactive={{true}}
								/>
							{{/if}}
						</span>
					{{/if}}
				{{/if}}
			</div>

			{{#unless this.isOverlayHidden}}
				<div class="overlay">
					{{#if @model.isWithCustomToken}}
						New token
					{{else}}
						<CommonUiIconComponent @name="Connect" />Connect
					{{/if}}
				</div>
			{{/unless}}

		</li>
	</template>
}
